/**
 * ExportPackageCard — the typed card for `prepare_export_package`
 * (ChatMessage.TOOL_CARDS; backend allowlist agent_types.RENDERED_TOOLS).
 *
 * Arc offers a browsable package (plans/BY_DOCUMENT_INVENTORY_VIEW_PROPOSAL.md
 * §7b-1): what it would hold, and a "Prepare download" button. Arc does not
 * start the export — export is a step-up action — so the button sends the
 * request the tool prepared to the same endpoint the export dialog uses; the
 * job shows in the background-jobs banner and the zip downloads when ready.
 * A refused request renders no card — Arc's prose says why.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { api } from '../api';
import { useBackgroundJobs } from './jobs/BackgroundJobsContext';

const S = {
  card: { marginTop: 8, border: '1px solid #d9cdf5', borderRadius: 8, background: '#faf8ff', overflow: 'hidden', fontSize: 13 },
  head: { padding: '8px 12px', background: '#efe8fd', fontWeight: 600 },
  body: { padding: '8px 12px', display: 'grid', gap: 4 },
  hint: { fontSize: 12, color: '#667' },
  actions: { display: 'flex', gap: 10, alignItems: 'center', padding: '8px 12px', borderTop: '1px solid #eee6fb', background: '#fdfcff', flexWrap: 'wrap' },
  done: { fontSize: 12, color: '#2c6e49', fontWeight: 600 },
  err: { color: '#c0392b', fontSize: 12 },
};

const fmtBytes = (n) => {
  if (!n) return '';
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${Math.round(n / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

export default function ExportPackageCard({ result }) {
  const { caseId } = useParams();
  const { submitJob } = useBackgroundJobs();
  const [state, setState] = useState('idle');          // idle | starting | started
  const [error, setError] = useState(null);
  if (!caseId || !result || !['ready', 'too_large'].includes(result.status)) return null;

  const n = result.document_count || 0;
  const cut = result.cut_from_files || 0;
  const titles = result.first_titles || [];
  const tooLarge = result.status === 'too_large';

  async function start() {
    setState('starting');
    setError(null);
    try {
      await submitJob('export_documents', api.post(`/documents/case/${caseId}/export`, result.request));
      setState('started');
    } catch (e) {
      setError(e?.message || 'Could not start the export.');
      setState('idle');
    }
  }

  return (
    <div style={S.card} data-testid="export-package-card">
      <div style={S.head}>Package — {n} document{n === 1 ? '' : 's'} from {result.label}</div>
      <div style={S.body}>
        <span>
          One zip: an index page you open in a browser (works offline) and each document as its own numbered file
          {result.original_files ? `, plus ${result.original_files} original file${result.original_files === 1 ? '' : 's'}` : ''}.
          {' '}{[cut ? `${cut} cut out of larger files` : '', fmtBytes(result.total_bytes)].filter(Boolean).join(' · ')}
        </span>
        {titles.length > 0 && (
          <span style={S.hint}>
            Starts with: {titles.join(' · ')}{n > titles.length ? ` · … ${n - titles.length} more` : ''}
          </span>
        )}
        {tooLarge && (
          <span style={S.err}>
            This is over the {fmtBytes(result.limit_bytes)} limit for one download. Ask for a folder, one file’s documents, or documents only.
          </span>
        )}
      </div>
      {!tooLarge && (
        <div style={S.actions}>
          {state === 'started' ? (
            <span style={S.done} data-testid="export-package-started">Preparing — it downloads when it is ready.</span>
          ) : (
            <button type="button" className="btn btn-sm btn-primary" data-testid="export-package-start" onClick={start} disabled={state === 'starting'}>
              {state === 'starting' ? 'Starting…' : '⬇ Prepare download'}
            </button>
          )}
          {error && <span style={S.err}>{error}</span>}
        </div>
      )}
    </div>
  );
}
